import { useCallback, useEffect, useState } from 'react';

import { createEngagementClient } from '../../../../src/services/client';
import { GET_ALL_FAVORITE_WORKOUTS, GET_FAVORITE_WORKOUT } from '../engagementGql/favoriteContent';
import { addErrorInDatadogRum } from '../utils/utilities';

export type FavoriteWorkoutContent = {
  __typename: 'Workout';
  categories: string[];
  name: string;
  imageWide: string;
  tiers: string[];
  durationInSeconds: number;
  equipment: string[];
  programName: string;
  completionCount: number;
};

type FavoriteWorkoutItem = {
  contentType: string;
  contentId: string;
  contentV2: FavoriteWorkoutContent;
  createdAt?: string;
};

type GetFavoriteWorkoutResponse = {
  items: FavoriteWorkoutItem[];
  itemsTotal: number;
  nextToken: string | null;
};

type CheckFavoriteWorkoutParams =
  | { programName: string; workoutName: string } // Both must exist
  | { programName?: string; workoutName?: undefined }; // Only program name or neither, never a case where workoutName exists without programName

const WORKOUT_CONTENT_TYPE = 'workout';
const PAGE_SIZE = 50;

const useCheckFavoriteWorkout = ({ programName, workoutName }: CheckFavoriteWorkoutParams) => {
  const [loading, setLoading] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteWorkouts, setFavoriteWorkouts] = useState<FavoriteWorkoutContent[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [itemsTotal, setItemsTotal] = useState<number>(0);

  const handleQuery = useCallback(
    async (token?: string) => {
      // For fetching specific workout, programName is sometimes intialised later and initially undefined
      if (workoutName && !programName) {
        return;
      }

      const contentId = workoutName
        ? `${programName}|${workoutName}` // Searches for specific workout
        : programName || null; // Searches for workouts in program

      const query = contentId ? GET_FAVORITE_WORKOUT : GET_ALL_FAVORITE_WORKOUTS;

      setLoading(true);
      try {
        const { data } = await createEngagementClient.query({
          query,
          variables: {
            contentType: WORKOUT_CONTENT_TYPE,
            ...(contentId
              ? { contentId } // Called from workout page
              : { pageSize: PAGE_SIZE, nextToken: token, syncVimeo: !token }), // Called from my workouts page, no token means first page and syncs
          },
        });

        const response: GetFavoriteWorkoutResponse = data?.favoriteContent;
        const items: FavoriteWorkoutItem[] = response?.items || [];

        if (workoutName) {
          setIsFavorited(items.length > 0);
        } else {
          const favoriteContentItems: FavoriteWorkoutContent[] = [...items]
            .sort((a, b) => {
              const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
              const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
              return dateB - dateA;
            })
            .map((item) => item.contentV2)
            .filter((content) => content != null);

          setFavoriteWorkouts((prev) =>
            token ? [...prev, ...favoriteContentItems] : favoriteContentItems
          );
          setNextToken(response?.nextToken ?? null);
          setItemsTotal(response?.itemsTotal ?? 0);
        }
      } catch (error) {
        addErrorInDatadogRum(error);
      } finally {
        setLoading(false);
      }
    },
    [workoutName, programName]
  );

  const fetchNextPage = useCallback(async () => {
    if (!nextToken || loading || workoutName) return;
    await handleQuery(nextToken);
  }, [nextToken, loading, workoutName, handleQuery]);

  useEffect(() => {
    setFavoriteWorkouts([]);
    setNextToken(null);
    setIsFavorited(false);
    handleQuery();
  }, [handleQuery]);

  return {
    favoriteWorkouts,
    loading,
    nextToken,
    fetchNextPage,
    refetch: () => handleQuery(),
    isFavorited,
    itemsTotal,
  };
};

export default useCheckFavoriteWorkout;
